/* Generic CRUD list/edit table used by all admin pages. */
import { useCallback, useEffect, useState } from 'react';
import { Plus, Trash2, Pencil, Save, X, Eye } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

// `itemLabel` is the singular name used in "New X" / "Edit X" modal titles —
// pass it explicitly (e.g. "Portfolio item", "Pricing plan") instead of
// guessing by chopping a letter off `title`, which breaks for any title that
// doesn't simply end in "s" (it turned "Portfolio" into "Portfoli").
// `publicPath(item)` is optional: return the live site URL for a row (e.g.
// `/portfolio/${item.slug}`) to show a "View live" button, or null/undefined
// to hide it for that row (e.g. draft items with no slug yet).
export default function CrudTable({ table, title, itemLabel, columns, defaults, render, publicPath }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase.from(table).select('*').order('created_at', { ascending: false });
    if (error) toast.error(error.message);
    setItems(data || []);
    setLoading(false);
  }, [table]);
  useEffect(() => { load(); }, [load]);

  const save = async (row) => {
    const payload = { ...row };
    delete payload._isNew;
    if (row._isNew) {
      delete payload.id;
      const { error } = await supabase.from(table).insert([payload]);
      if (error) return toast.error(error.message);
      toast.success('Created');
    } else {
      const { error } = await supabase.from(table).update(payload).eq('id', row.id);
      if (error) return toast.error(error.message);
      toast.success('Saved');
    }
    setEditing(null); load();
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    const { error } = await supabase.from(table).delete().eq('id', id);
    if (error) return toast.error(error.message);
    toast.success('Deleted'); load();
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl text-ink-900">{title}</h1>
          <p className="mt-1 text-sm text-ink-500">{items.length} items</p>
        </div>
        <button data-testid={`admin-new-${table}`} onClick={() => setEditing({ ...defaults, _isNew: true })} className="btn-cyan inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium">
          <Plus className="h-4 w-4" /> New
        </button>
      </div>

      <div className="mt-8 overflow-hidden rounded-2xl border border-ink-100 bg-white">
        <div className="grid grid-cols-12 border-b border-ink-100 bg-ink-50/60 px-5 py-3 text-[11px] uppercase tracking-[0.2em] text-ink-500">
          {columns.map(c => <div key={c.key} className={c.className || 'col-span-3'}>{c.label}</div>)}
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {loading && <div className="p-8 text-center text-ink-500">Loading…</div>}
        {!loading && items.length === 0 && <div className="p-8 text-center text-ink-500">No items yet. Click <b>New</b> to add one.</div>}
        {items.map(it => (
          <div key={it.id} className="grid grid-cols-12 items-center border-b border-ink-100 px-5 py-4 text-sm">
            {columns.map(c => <div key={c.key} className={c.className || 'col-span-3'}>{(c.format ? c.format(it[c.key], it) : it[c.key]) ?? <span className="text-ink-300">—</span>}</div>)}
            <div className="col-span-2 flex justify-end gap-2">
              {publicPath && publicPath(it) && (
                <a href={publicPath(it)} target="_blank" rel="noopener noreferrer" title="View live" className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-ink-100 text-ink-500 hover:border-cyan hover:text-cyan"><Eye className="h-3.5 w-3.5" /></a>
              )}
              <button onClick={() => setEditing(it)} className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-ink-100 text-ink-500 hover:border-cyan hover:text-cyan"><Pencil className="h-3.5 w-3.5" /></button>
              <button onClick={() => remove(it.id)} className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-ink-100 text-ink-500 hover:border-red-500 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setEditing(null)}>
          <div className="w-full max-w-2xl rounded-3xl bg-white shadow-2xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()} data-lenis-prevent>
            <div className="flex items-center justify-between px-8 pt-8">
              <h2 className="font-display text-2xl">{editing._isNew ? `New ${itemLabel || title}` : `Edit ${itemLabel || title}`}</h2>
              <button onClick={() => setEditing(null)}><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={e => { e.preventDefault(); save(editing); }} className="flex-1 overflow-y-auto px-8 py-6 space-y-4">
              {render(editing, setEditing)}
              <div className="flex justify-end gap-3 pt-4 sticky bottom-0 bg-white -mx-8 px-8 border-t border-ink-100 mt-6 py-4">
                <button type="button" onClick={() => setEditing(null)} className="rounded-full border border-ink-100 px-5 py-2 text-sm">Cancel</button>
                <button type="submit" className="btn-cyan inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-medium"><Save className="h-4 w-4" /> Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export function TextField({ value, onChange, label, type = 'text', required }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] text-ink-400">{label}</label>
      <input required={required} type={type} value={value ?? ''} onChange={e => onChange(type === 'number' ? Number(e.target.value) : e.target.value)} className="mt-1.5 w-full rounded-xl border border-ink-100 bg-ink-50/40 px-4 py-2.5 text-sm focus:border-cyan focus:outline-none focus:ring-2 focus:ring-cyan/20" />
    </div>
  );
}
export function TextArea({ value, onChange, label, rows = 4 }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] text-ink-400">{label}</label>
      <textarea rows={rows} value={value ?? ''} onChange={e => onChange(e.target.value)} className="mt-1.5 w-full rounded-xl border border-ink-100 bg-ink-50/40 px-4 py-2.5 text-sm focus:border-cyan focus:outline-none focus:ring-2 focus:ring-cyan/20" />
    </div>
  );
}
export function SelectField({ value, onChange, label, options, required }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] text-ink-400">{label}</label>
      <select required={required} value={value ?? ''} onChange={e => onChange(e.target.value)} className="mt-1.5 w-full rounded-xl border border-ink-100 bg-ink-50/40 px-4 py-2.5 text-sm focus:border-cyan focus:outline-none focus:ring-2 focus:ring-cyan/20">
        <option value="" disabled>Select…</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
export function CheckField({ value, onChange, label }) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <input type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)} className="h-4 w-4 accent-cyan" /> {label}
    </label>
  );
}
/* Resize + compress an image in the browser before upload — keeps admin-uploaded
   photos from bloating page weight. Caps the longest edge at maxDim and re-encodes
   as JPEG at `quality`. No external library needed (pure Canvas API). */
function compressImage(file, maxDim = 1600, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read file'));
    reader.onload = (e) => {
      const img = new window.Image();
      img.onerror = () => reject(new Error('Could not read image'));
      img.onload = () => {
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          if (width > height) { height = Math.round(height * (maxDim / width)); width = maxDim; }
          else { width = Math.round(width * (maxDim / height)); height = maxDim; }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, width, height); // flatten transparency
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('Compression failed')), 'image/jpeg', quality);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

/* Upload widget: picks a file, compresses it client-side, uploads to the
   Supabase `media` storage bucket, and fills the field with the public URL.
   A manual URL input stays underneath as a fallback / for editing directly. */
export function ImageField({ value, onChange, label, folder = 'uploads' }) {
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file later
    if (!file) return;
    setUploading(true);
    try {
      const blob = await compressImage(file);
      const path = `${folder}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.jpg`;
      const { error: upErr } = await supabase.storage.from('media').upload(path, blob, { contentType: 'image/jpeg', upsert: false });
      if (upErr) throw upErr;
      const { data } = supabase.storage.from('media').getPublicUrl(path);
      onChange(data.publicUrl);
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] text-ink-400">{label}</label>
      <div className="mt-1.5 flex items-start gap-3">
        {value ? (
          <img src={value} alt="" className="h-16 w-16 shrink-0 rounded-lg border border-ink-100 object-cover" />
        ) : (
          <div className="grid h-16 w-16 shrink-0 place-items-center rounded-lg border border-dashed border-ink-200 text-[10px] text-ink-300">No image</div>
        )}
        <div className="flex-1 space-y-2">
          <input type="file" accept="image/*" onChange={handleFile} disabled={uploading} className="block w-full text-xs text-ink-500 file:mr-3 file:rounded-lg file:border-0 file:bg-cyan-soft file:px-3 file:py-2 file:text-xs file:font-medium file:text-cyan-700 hover:file:bg-cyan/20" />
          <input type="text" placeholder="or paste an image URL" value={value ?? ''} onChange={e => onChange(e.target.value)} className="w-full rounded-xl border border-ink-100 bg-ink-50/40 px-3 py-2 text-xs focus:border-cyan focus:outline-none" />
          {uploading && <p className="text-xs text-cyan">Uploading…</p>}
        </div>
      </div>
    </div>
  );
}

export function JsonField({ value, onChange, label }) {
  const [text, setText] = useState(JSON.stringify(value ?? [], null, 2));
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] text-ink-400">{label} (JSON)</label>
      <textarea rows={5} value={text} onChange={e => { setText(e.target.value); try { onChange(JSON.parse(e.target.value)); } catch (err) { /* invalid JSON, ignore */ } }} className="mt-1.5 w-full rounded-xl border border-ink-100 bg-ink-50/40 px-4 py-2.5 text-xs font-mono focus:border-cyan focus:outline-none" />
    </div>
  );
}
